/**
 * Workstations — queries + mutation runners.
 *
 * Tenant-scoped. Feature code targets the canonical platform path shape and the api-client rewrites
 * it to /api/tenant/v1/workstations on a tenant host, which is where the backend mounts this
 * surface. There is no {tenant_id} segment on the tenant-host tree — the tenant comes from the
 * session and the Host header — so the id in the path below exists only to feed that rewriter.
 *
 * THE READS DO NOT GO THROUGH withStepUp, AND THAT IS A BACKEND FACT, NOT A PREFERENCE. Reads are
 * mounted behind `workstations.read`, which is not in the step-up set; only the writes sit behind
 * `workstations.manage`, which is. Inherited whole from the gateways surface, including the reason
 * it matters: if a read ever moves back behind a step-up permission, the wrapper has to come back
 * WITH a serializing queue and a polling ban, because the step-up store rejects a second concurrent
 * challenge and a page that fans out reads would fail its second one for a reason nobody can see.
 *
 * THE SECRETS RULE has its home in this file: the enrolment-token mint resolves with the raw token,
 * and no `onSuccess` here may ever `setQueryData` a response carrying it. Invalidate the roster;
 * let the plaintext live in the promise the caller awaits and nowhere else.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { withStepUp } from "@/lib/with-step-up";
import {
  effectivePolicySchema,
  enrollmentTokensResponseSchema,
  mintedTokenSchema,
  policyImpactSchema,
  policyListResponseSchema,
  policyWriteResultSchema,
  revokedWorkstationSchema,
  approvedWorkstationSchema,
  approveWorkstationsSchema,
  TOKEN_LIFETIME_SECONDS,
  workstationEventsResponseSchema,
  workstationsResponseSchema,
  type EffectivePolicy,
  type EnrollmentToken,
  type MintTokenForm,
  type MintedToken,
  type PolicyImpact,
  type PolicyListResponse,
  type PolicyWriteResult,
  type RevokedWorkstation,
  type ApprovedWorkstation,
  type ApproveWorkstationsResult,
  type WorkstationEventsResponse,
  type WorkstationsResponse,
} from "./schemas";

function base(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/workstations`;
}

/**
 * Shared read options: how live this surface is.
 *
 * 60s, matching the devices and gateways surfaces. The moving fact here is convergence — a machine
 * that has never fetched a document may fetch one while an operator is watching the page, and a
 * console frozen at mount would keep reporting a machine as stranded after it recovered.
 */
const LIVE_READ_OPTIONS = {
  refetchInterval: 60_000,
} as const;

// ── Reads ─────────────────────────────────────────────────────────────────

/**
 * The machine roster.
 *
 * Note the trailing slash: the handler registers `GET /` INSIDE the /workstations route, so the
 * collection lives at `/workstations/` and a request without it does not reach this handler.
 */
/*
 * THE WHOLE ENVELOPE, not just `.workstations`. The response also carries the `principals`
 * name-resolution sidecar that turns the "Authorised by" column's raw user ids into names, and
 * unwrapping to the array here would throw it away.
 */
export async function fetchWorkstations(tenantID: string): Promise<WorkstationsResponse> {
  const raw = await apiFetch<unknown>(`${base(tenantID)}/`);
  return workstationsResponseSchema.parse(raw);
}

/** The roster, and with it the convergence answer this page exists to give. */
export function useWorkstationsQuery(tenantID: string) {
  return useQuery<WorkstationsResponse, ApiError>({
    queryKey: queryKeys.workstations.list(tenantID),
    queryFn: () => fetchWorkstations(tenantID),
    enabled: tenantID.length > 0,
    ...LIVE_READ_OPTIONS,
  });
}

export async function fetchEnrollmentTokens(tenantID: string): Promise<EnrollmentToken[]> {
  const raw = await apiFetch<unknown>(`${base(tenantID)}/enrollment-tokens`);
  return enrollmentTokensResponseSchema.parse(raw).tokens;
}

/**
 * The enrolment-token roster.
 *
 * No polling timer, unlike the machine roster: a token list changes only when a person mints,
 * withdraws or redeems one — except for expiry, which the SERVER derives from its own clock on
 * every read. So the app-wide focus refetch is what keeps it honest, and a row that expires while
 * the tab sits idle is corrected the moment the operator returns to it.
 */
export function useEnrollmentTokensQuery(tenantID: string) {
  return useQuery<EnrollmentToken[], ApiError>({
    queryKey: queryKeys.workstations.tokens(tenantID),
    queryFn: () => fetchEnrollmentTokens(tenantID),
    enabled: tenantID.length > 0,
  });
}

// ── Policy reads ──────────────────────────────────────────────────────────

/**
 * The stored overrides, plus the settable vocabulary.
 *
 * `settable_paths` is served precisely so a console never hardcodes a second copy of what may be
 * set. policy-fields.ts adds presentation for those paths and handles both directions of drift.
 */
export async function fetchPolicy(tenantID: string): Promise<PolicyListResponse> {
  const raw = await apiFetch<unknown>(`${base(tenantID)}/policy`);
  return policyListResponseSchema.parse(raw);
}

export function usePolicyQuery(tenantID: string) {
  return useQuery<PolicyListResponse, ApiError>({
    queryKey: queryKeys.workstations.policy(tenantID),
    queryFn: () => fetchPolicy(tenantID),
    enabled: tenantID.length > 0,
  });
}

/**
 * The resolved document for ONE machine, with per-field provenance and every suppression.
 *
 * A 409 here is `stored_patch_invalid` and is NOT swallowed. It means a stored patch no longer
 * validates, so this machine is currently being served 503s and running on its cached document —
 * and the admin reading this page is the person who can fix it. `retry: false` because a stored
 * patch will not become valid on a second attempt, and two more requests only delay the message.
 */
export async function fetchEffectivePolicy(
  tenantID: string,
  deviceID: string,
): Promise<EffectivePolicy> {
  const raw = await apiFetch<unknown>(
    `${base(tenantID)}/policy/effective/${encodeURIComponent(deviceID)}`,
  );
  return effectivePolicySchema.parse(raw);
}

export function useEffectivePolicyQuery(tenantID: string, deviceID: string | undefined) {
  return useQuery<EffectivePolicy, ApiError>({
    queryKey: queryKeys.workstations.effective(tenantID, deviceID ?? ""),
    queryFn: () => fetchEffectivePolicy(tenantID, deviceID ?? ""),
    enabled: tenantID.length > 0 && !!deviceID,
    retry: false,
  });
}

/**
 * The fleet view: blast radius, the shielded machines, and the warn-and-allow counts.
 *
 * Read BEFORE the exclusiveMode acknowledgement, not after — that is the whole reason the endpoint
 * exists. Every consumer must honour `truncated`: true means the answer is "of the first 500", and
 * a count rendered without it is a lie.
 */
export async function fetchPolicyImpact(tenantID: string): Promise<PolicyImpact> {
  const raw = await apiFetch<unknown>(`${base(tenantID)}/policy/impact`);
  return policyImpactSchema.parse(raw);
}

export function usePolicyImpactQuery(tenantID: string) {
  return useQuery<PolicyImpact, ApiError>({
    queryKey: queryKeys.workstations.impact(tenantID),
    queryFn: () => fetchPolicyImpact(tenantID),
    enabled: tenantID.length > 0,
  });
}

// ── The reported trail ────────────────────────────────────────────────────

/**
 * What the machines have reported about themselves, filtered by the correlation GUID and/or one
 * machine.
 *
 * THE CORRELATION GUID IS THE QUERY. It is what turns a client event and a server decision into one
 * story, and without it an incident means correlating timestamps across three trust boundaries.
 *
 * The rows arrive ordered by `received_at` and this surface keeps that order rather than re-sorting
 * on the claimed time: the claimed time is the CLIENT's clock, and a machine at a lock screen may
 * have no usable clock at all.
 */
export async function fetchWorkstationEvents(
  tenantID: string,
  filter: { correlationID?: string; deviceID?: string },
): Promise<WorkstationEventsResponse> {
  const raw = await apiFetch<unknown>(`${base(tenantID)}/events`, {
    searchParams: {
      correlation_id: filter.correlationID,
      device_id: filter.deviceID,
    },
  });
  return workstationEventsResponseSchema.parse(raw);
}

export function useWorkstationEventsQuery(
  tenantID: string,
  filter: { correlationID?: string; deviceID?: string },
) {
  return useQuery<WorkstationEventsResponse, ApiError>({
    queryKey: queryKeys.workstations.events(
      tenantID,
      filter.correlationID ?? "",
      filter.deviceID ?? "",
    ),
    queryFn: () => fetchWorkstationEvents(tenantID, filter),
    enabled: tenantID.length > 0,
  });
}

// ── Writes ────────────────────────────────────────────────────────────────

/**
 * The mint body, and every field the server's decoder will accept — it runs `DisallowUnknownFields`,
 * so an extra key is a 400 rather than an ignored field.
 *
 * A BOUND TOKEN SENDS ONLY `jkt`; A BATCH SENDS ONLY `batch` + `device_cap`. The server refuses any
 * mix (a thumbprint and batch cannot both be stated), and it refuses a bound mint carrying a cap
 * above 1. Building the body from the discriminant rather than from optional fields is what makes
 * those refusals unreachable from this form.
 */
function mintBody(input: MintTokenForm): Record<string, unknown> {
  const body: Record<string, unknown> = {
    ttl_seconds: TOKEN_LIFETIME_SECONDS[input.lifetime],
  };
  if (input.mode === "bound") {
    body.jkt = input.jkt;
  } else {
    // Stated explicitly and audited by name. There is no "unbound single-use" token on this plane;
    // the imaging case is this variant and nothing else.
    body.batch = true;
    body.device_cap = input.device_cap;
  }
  return body;
}

/**
 * Mint. Resolves with the raw token exactly once.
 *
 * THE RESOLVED VALUE IS DELIBERATELY NOT WRITTEN INTO ANY CACHE. `onSuccess` invalidates the token
 * roster so the new row appears; it never `setQueryData`s the response, because the response
 * carries the plaintext. The secret lives in the promise the caller awaits, is held in one
 * component's state for one reveal, and is dropped.
 */
export function useMintTokenMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<MintedToken, ApiError, MintTokenForm>({
    mutationFn: (input) =>
      withStepUp(async () => {
        const raw = await apiFetch<unknown>(`${base(tenantID)}/enrollment-tokens`, {
          method: "POST",
          // NOT JSON.stringify'd: apiFetch serializes `body` itself, and double-stringifying sends
          // a JSON string where the server expects an object (check-api-body.mjs).
          body: mintBody(input),
        });
        return mintedTokenSchema.parse(raw);
      }),
    onSuccess: () => {
      // Invalidate only. NEVER setQueryData here: the resolved value carries the secret.
      void qc.invalidateQueries({ queryKey: queryKeys.workstations.tokens(tenantID) });
    },
  });
}

/**
 * Withdraw a pending token.
 *
 * The server answers 409 `not_pending` for an unknown id, an already-redeemed token and an
 * already-revoked one alike, and that conflation is deliberate on its side. The caller distinguishes
 * on status, which the roster already carries — so this mutation reports the refusal rather than
 * paraphrasing it as a failure, because a revoke that races a redemption really did withdraw
 * nothing.
 */
export function useRevokeTokenMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { tokenID: string }>({
    mutationFn: ({ tokenID }) =>
      withStepUp(async () => {
        await apiFetch<void>(
          `${base(tenantID)}/enrollment-tokens/${encodeURIComponent(tokenID)}/revoke`,
          { method: "POST" },
        );
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.workstations.tokens(tenantID) });
    },
  });
}

/**
 * Cut a machine off the device plane.
 *
 * TERMINAL AND IDEMPOTENT, and the two facts pull in opposite directions on the caller's side. It is
 * terminal, so the dialog behind it is a typed-phrase confirmation rather than a click. It is
 * idempotent, so a repeat is a 200 carrying `changed: false` and not the token surface's 409 —
 * there is no error path here for "already revoked", and inventing one would train an operator to
 * read "this desktop is cut off" as a failure.
 *
 * NO CASCADE, and the copy on this surface has to say so: the users who signed in on that machine
 * keep their credentials and their issued logon certificates. Revoking a machine is not disabling a
 * person. The invalidation is therefore the workstation subtree only.
 */
export function useRevokeWorkstationMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<RevokedWorkstation, ApiError, { deviceID: string }>({
    mutationFn: ({ deviceID }) =>
      withStepUp(async () => {
        const raw = await apiFetch<unknown>(
          `${base(tenantID)}/${encodeURIComponent(deviceID)}/revoke`,
          { method: "POST" },
        );
        return revokedWorkstationSchema.parse(raw);
      }),
    onSuccess: () => {
      // The whole subtree: a revoked machine leaves the fleet's blast-radius counts as well as the
      // roster, and a page showing it revoked in one panel and counted in another is the
      // disagreement /policy/impact exists to prevent.
      void qc.invalidateQueries({ queryKey: queryKeys.workstations.all(tenantID) });
    },
  });
}

/**
 * Admit one self-enrolled machine.
 *
 * THROUGH withStepUp, because this sits behind `workstations.manage` like every other write on this
 * surface. Admitting a machine is exactly the act that lets the two lockout primitives reach it, so
 * it is not a write anyone should be able to make on a stale session.
 *
 * IDEMPOTENT, and the response says so rather than erroring: a second approval comes back
 * `already_approved` with `changed: false`. There is deliberately no error path for it — inventing
 * one would train an operator to read "this machine is admitted" as a failure, which is the same
 * mistake the revoke surface avoids.
 */
export function useApproveWorkstationMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<ApprovedWorkstation, ApiError, { deviceID: string }>({
    mutationFn: ({ deviceID }) =>
      withStepUp(async () => {
        const raw = await apiFetch<unknown>(
          `${base(tenantID)}/${encodeURIComponent(deviceID)}/approve`,
          { method: "POST" },
        );
        return approvedWorkstationSchema.parse(raw);
      }),
    onSuccess: () => {
      // THE WHOLE SUBTREE, not just the roster. A promotion changes which policy the machine is
      // actually served — /policy/impact and the effective-policy panel both answer differently
      // afterwards — and a page showing it approved in one panel and still shielded in another is
      // precisely the disagreement the impact endpoint exists to prevent.
      void qc.invalidateQueries({ queryKey: queryKeys.workstations.all(tenantID) });
    },
  });
}

/**
 * Rung 3 in bulk — the queue action, and the one that makes Rung 0 workable at fleet scale.
 *
 * THIS IS THE POINT OF THE FEATURE. Zero-touch enrolment means an estate arrives as `self_enrolled`
 * machines, and §4.1's bound 3 withholds the lockout primitives from every one of them. Approving
 * one at a time is not a fleet workflow; approving a selection is.
 *
 * EVERY PER-MACHINE OUTCOME IS A SUCCESS. The resolved value carries the breakdown — approved,
 * already_approved, not_pending, not_active, unknown — and the caller words its confirmation from
 * the counts. A partially stale selection is ordinary and must not present as a failed request.
 */
export function useApproveWorkstationsMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<ApproveWorkstationsResult, ApiError, { deviceIDs: readonly string[] }>({
    mutationFn: ({ deviceIDs }) =>
      withStepUp(async () => {
        const raw = await apiFetch<unknown>(`${base(tenantID)}/approve`, {
          method: "POST",
          body: { device_ids: deviceIDs },
        });
        return approveWorkstationsSchema.parse(raw);
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.workstations.all(tenantID) });
    },
  });
}

/**
 * Write one scope's overrides.
 *
 * IT IS A WHOLE-PATCH WRITE, NOT A MERGE. The body IS the new override set for that scope: a path
 * absent from it is unset, and an empty patch clears the override entirely. That is the only way an
 * admin can ever REMOVE an override, and it is why the editor always sends its complete draft
 * rather than a diff. A merge-semantics call would make "unset this field" unexpressible, which is
 * the same lost distinction the sparse storage exists to preserve.
 *
 * `acknowledge_exclusive_mode` is sent ONLY when the patch enables exclusiveMode, because the
 * server gates only that direction — disabling it needs nothing. Sending the acknowledgement
 * unconditionally would turn an explicit act into a constant, which is precisely what "not a
 * checkbox adjacent to ordinary settings" rules out.
 */
export interface PolicyWriteInput {
  /** The complete override set for this scope. Empty clears it. */
  patch: Record<string, unknown>;
  /** Absent for the tenant default; a device id for a per-machine override. */
  deviceID?: string;
  acknowledgeExclusiveMode?: boolean;
}

export function useSetPolicyMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<PolicyWriteResult, ApiError, PolicyWriteInput>({
    mutationFn: ({ patch, deviceID, acknowledgeExclusiveMode }) =>
      withStepUp(async () => {
        const path = deviceID
          ? `${base(tenantID)}/policy/devices/${encodeURIComponent(deviceID)}`
          : `${base(tenantID)}/policy`;
        const body: Record<string, unknown> = { patch };
        if (acknowledgeExclusiveMode) body.acknowledge_exclusive_mode = true;
        const raw = await apiFetch<unknown>(path, { method: "PUT", body });
        return policyWriteResultSchema.parse(raw);
      }),
    onSuccess: () => {
      // EVERYTHING under this tenant, not just the row that changed. A tenant edit moves every
      // machine's effective document and every machine's policy revision, and a device edit moves
      // the fleet's impact counts — so a narrower invalidation would leave one of the two views on
      // this page contradicting the other.
      void qc.invalidateQueries({ queryKey: queryKeys.workstations.all(tenantID) });
    },
  });
}
